export const getEncounterSectionTitle = (sectionKey: string): string => {
    const sectionTitleMap: Record<string, string> = {
        enctrInsuranceDoc: 'Insurance',
        enctrDiagnosisDoc: 'Diagnosis',
        enctrRelationsDoc: 'Relations',
        enctrGuarantorDoc: 'Guarantor',
    };

    return (
        sectionTitleMap[sectionKey] ||
        sectionKey
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase())
            .trim()
    );
};

export const getEncounterArrayTitle = (arrayKey: string, item: any, index: number): string => {
    if (arrayKey === 'enctrInsuranceDoc') {
        return item.insurerName || item.insuredLastName || item.policyNumber || `Insurance ${index + 1}`;
    }

    if (arrayKey === 'enctrDiagnosisDoc') {
        return item.diagDesc || item.diagCode || `Diagnosis ${index + 1}`;
    }

    if (arrayKey === 'enctrRelationsDoc') {
        const name = [item.firstName, item.lastName].filter(Boolean).join(' ') || item.relToPatient;
        return name || `Relation ${index + 1}`;
    }

    if (arrayKey === 'enctrGuarantorDoc') {
        const name = [item.firstName, item.lastName].filter(Boolean).join(' ') || item.relToPatient;
        return name || `Guarantor ${index + 1}`;
    }

    return item.insurerName || item.diagDesc || item.lastName || item.firstName || `Item ${index + 1}`;
};

// Label mapping - customize labels here without affecting data binding
// The keys are the actual field names used for data binding
export const getEncounterFieldLabel = (fieldKey: string): string => {
    const labelMap: Record<string, string> = {
        // Patient Information
        patFrstNm: 'First Name',
        patMidNm: 'Middle Name',
        patLastNm: 'Last Name',
        patMrn: 'MRN',
        patSsn: 'SSN',
        patDob: 'Date of Birth',
        patGenderCd: 'Gender',
        patZipCd: 'Zip Code',
        patAddrLn1: 'Address Line 1',
        patAddrLn2: 'Address Line 2',
        patCity: 'City',
        patStateCd: 'State',
        patPhNoHome: 'Home Phone',
        patPhNoBus: 'Business Phone',
        patPhNoMob: 'Mobile Phone',
        patEmailHome: 'Home Email',
        patEmailBus: 'Business Email',
        patDeceasedInd: 'Deceased',
        patDeceasedDt: 'Deceased Date',
        // Encounter Information
        enctrNo: 'Encounter Number',
        admitDt: 'Admission Date',
        admitTm: 'Admission Time',
        dischDt: 'Discharge Date',
        dischTm: 'Discharge Time',
        enctrAdmitTyp: 'Admit Type',
        enctrPatClass: 'Patient Class',
        enctrPatTyp: 'Patient Type',
        enctrHospSvcCd: 'Hospital Service Code',
        // Accident Information
        accDt: 'Accident Date',
        accCd: 'Accident Code',
        accDesc: 'Accident Description',
        accLocn: 'Accident Location',
        accAutoStCd: 'Auto State Code',
        accAutoStNm: 'Auto State Name',
        accJobRelInd: 'Job Related',
        accDeathInd: 'Death Indicator',
        // Insurance fields
        id: 'ID',
        insurancePlanId: 'Insurance Plan ID',
        insurerId: 'Insurer ID',
        insurerName: 'Insurer Name',
        insurerAddress1: 'Insurer Address 1',
        insurerAddress2: 'Insurer Address 2',
        insurerCity: 'Insurer City',
        insurerPostalCode: 'Insurer Postal Code',
        insurerStateAbbrev: 'Insurer State',
        insurerContactPerson: 'Insurer Contact Person',
        insurerPhone: 'Insurer Phone',
        insuredId: 'Insured ID',
        insuredGroupEmplId: 'Group/Employer ID',
        insuredGroupEmplName: 'Group/Employer Name',
        insuredFirstName: 'Insured First Name',
        insuredLastName: 'Insured Last Name',
        insuredMiddleName: 'Insured Middle Name',
        insuredSuffix: 'Insured Suffix',
        insuredRelToPatient: 'Relationship to Patient',
        insuredDOB: 'Insured Date of Birth',
        insuredSSN: 'Insured SSN',
        insuredAddress1: 'Insured Address 1',
        insuredAddress2: 'Insured Address 2',
        insuredCity: 'Insured City',
        insuredPostalCode: 'Insured Postal Code',
        insuredStateAbbrev: 'Insured State',
        insuredPhone: 'Insured Phone',
        insuredEmail: 'Insured Email',
        groupNumber: 'Group Number',
        groupName: 'Group Name',
        planEffectiveDate: 'Plan Effective Date',
        planExpirationDate: 'Plan Expiration Date',
        authorizationCode: 'Authorization Code',
        authorizationDate: 'Authorization Date',
        planType: 'Plan Type',
        companyPlanCode: 'Company Plan Code',
        policyNumber: 'Policy Number',
        policyLimitAmount: 'Policy Limit Amount',
        policyLimitDays: 'Policy Limit Days',
        verificationDateTime: 'Verification Date/Time',
        verificationBy: 'Verification By',
        verificationStatus: 'Verification Status',
        // Diagnosis fields
        codingMethod: 'Coding Method',
        diagCode: 'Diagnosis Code',
        diagDesc: 'Diagnosis Description',
        diagDate: 'Diagnosis Date',
        diagTime: 'Diagnosis Time',
        diagType: 'Diagnosis Type',
        // Relations fields
        lastName: 'Last Name',
        firstName: 'First Name',
        middleName: 'Middle Name',
        address1: 'Address Line 1',
        address2: 'Address Line 2',
        city: 'City',
        stateAbbrev: 'State',
        postalCode: 'Postal Code',
        countryCode: 'Country Code',
        homePhone: 'Home Phone',
        businessPhone: 'Business Phone',
        maritalStatus: 'Marital Status',
        gender: 'Gender',
        dob: 'Date of Birth',
        primaryLanguage: 'Primary Language',
        jobTitle: 'Job Title',
        jobStatus: 'Job Status',
        relToPatient: 'Relationship to Patient',
        ssn: 'SSN',
        // Guarantor fields
        addressLine1: 'Address Line 1',
        addressLine2: 'Address Line 2',
        employmentStatus: 'Employment Status',
        type: 'Type',
        occupation: 'Occupation',
        employerLastName: 'Employer Last Name',
        employerFirstName: 'Employer First Name',
        employerMiddleName: 'Employer Middle Name',
        employerAddress1: 'Employer Address 1',
        employerAddress2: 'Employer Address 2',
        employerPostalCode: 'Employer Postal Code',
        employerCity: 'Employer City',
        employerStateAbbrev: 'Employer State',
        employerCountryCode: 'Employer Country Code',
        employerPhone: 'Employer Phone',
    };

    // Return custom label if exists, otherwise generate from field key
    return (
        labelMap[fieldKey] ||
        fieldKey
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase())
            .trim()
    );
};
